import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { InvoiceRemindersTable } from "@/components/InvoiceRemindersTable";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function InvoiceRemindersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [organisationIdInput, setOrganisationIdInput] = useState(
    searchParams.get("organisationId") ?? "",
  );
  const [invoiceIdInput, setInvoiceIdInput] = useState(searchParams.get("invoiceId") ?? "");
  const debouncedOrganisationId = useDebouncedValue(organisationIdInput.trim());
  const debouncedInvoiceId = useDebouncedValue(invoiceIdInput.trim());

  useEffect(() => {
    setSearchParams(
      (params) => {
        if (debouncedOrganisationId) {
          params.set("organisationId", debouncedOrganisationId);
        } else {
          params.delete("organisationId");
        }
        if (debouncedInvoiceId) {
          params.set("invoiceId", debouncedInvoiceId);
        } else {
          params.delete("invoiceId");
        }
        return params;
      },
      { replace: true },
    );
  }, [debouncedOrganisationId, debouncedInvoiceId, setSearchParams]);

  // The API expects uuid-format values — only apply a filter once it's a full UUID.
  const organisationId = UUID_PATTERN.test(debouncedOrganisationId)
    ? debouncedOrganisationId
    : undefined;
  const invoiceId = UUID_PATTERN.test(debouncedInvoiceId) ? debouncedInvoiceId : undefined;

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <h1 className="mb-6 text-2xl font-bold">Invoice Reminders</h1>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          placeholder="Filter by organisation ID…"
          value={organisationIdInput}
          onChange={(e) => setOrganisationIdInput(e.target.value)}
          aria-invalid={!!debouncedOrganisationId && !organisationId}
          spellCheck={false}
          autoComplete="off"
          className="w-80 font-mono text-xs"
        />
        <Input
          placeholder="Filter by invoice ID…"
          value={invoiceIdInput}
          onChange={(e) => setInvoiceIdInput(e.target.value)}
          aria-invalid={!!debouncedInvoiceId && !invoiceId}
          spellCheck={false}
          autoComplete="off"
          className="w-80 font-mono text-xs"
        />
      </div>

      <InvoiceRemindersTable
        organisationId={organisationId}
        invoiceId={invoiceId}
        showAllColumns
      />
    </div>
  );
}
